import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdClose,
  MdEdit,
  MdDelete,
  MdAdd,
  MdSearch,
  MdFilterList,
  MdLocationOn,
  MdCalendarToday,
  MdPeople,
  MdEvent,
} from "react-icons/md";
import { FaChevronLeft, FaChevronRight, FaCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

const AUDIENCES = ["All", "Student", "Staff", "Parent", "Public"];

// ── helpers ──────────────────────────────────────────────────────────────────
function fmtDate(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}
function fmtDateTime(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
function toInputDatetime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

// ── Audience Badge ────────────────────────────────────────────────────────────
const AUDIENCE_COLORS = {
  All: "bg-blue-100 text-blue-700",
  Student: "bg-green-100 text-green-700",
  Staff: "bg-orange-100 text-orange-700",
  Parent: "bg-purple-100 text-purple-700",
  Public: "bg-slate-100 text-slate-600",
};
function AudienceBadge({ audience }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${AUDIENCE_COLORS[audience] || "bg-slate-100 text-slate-600"}`}>
      <MdPeople className="text-xs" /> {audience}
    </span>
  );
}

// ── Event Form Modal ──────────────────────────────────────────────────────────
function EventModal({ isOpen, onClose, onSuccess, token, event }) {
  const isEdit = !!event;
  const empty = { title: "", description: "", eventDate: "", eventEndDate: "", location: "", targetAudience: "All", coverImageUrl: "", attachmentUrl: "", isActive: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(event ? {
        title: event.title || "",
        description: event.description || "",
        eventDate: toInputDatetime(event.eventDate),
        eventEndDate: toInputDatetime(event.eventEndDate),
        location: event.location || "",
        targetAudience: event.targetAudience || "All",
        coverImageUrl: event.coverImageUrl || "",
        attachmentUrl: event.attachmentUrl || "",
        isActive: event.isActive ?? true,
      } : empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, event]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!form.description.trim()) return toast.error("Description is required.");
    if (!form.eventDate) return toast.error("Event date is required.");

    setSaving(true);
    try {
      const payload = {
        ...form,
        eventDate: new Date(form.eventDate).toISOString(),
        eventEndDate: form.eventEndDate ? new Date(form.eventEndDate).toISOString() : null,
      };
      if (isEdit) {
        await axios.put(`${server}/events/${event.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Event updated!");
      } else {
        await axios.post(`${server}/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Event created!");
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save event.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.93, opacity: 0, y: 24 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.93, opacity: 0, y: 24 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
                <MdEvent />
              </div>
              <h2 className="text-lg font-bold text-slate-800">{isEdit ? "Edit Event" : "New Event"}</h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors">
              <MdClose />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Title */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">Title *</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="Event title..." required
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">Description *</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                placeholder="Describe the event..." required rows={3}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none" />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">Start Date & Time *</label>
                <input type="datetime-local" value={form.eventDate} onChange={(e) => set("eventDate", e.target.value)} required
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">End Date & Time</label>
                <input type="datetime-local" value={form.eventEndDate} onChange={(e) => set("eventEndDate", e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">Location</label>
              <input value={form.location} onChange={(e) => set("location", e.target.value)}
                placeholder="Venue / address..."
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
            </div>

            {/* Audience */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-2">Target Audience</label>
              <div className="flex flex-wrap gap-2">
                {AUDIENCES.map((a) => (
                  <button key={a} type="button" onClick={() => set("targetAudience", a)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${form.targetAudience === a ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>

            {/* Cover Image URL */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">Cover Image URL</label>
              <input value={form.coverImageUrl} onChange={(e) => set("coverImageUrl", e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
            </div>

            {/* Attachment URL */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-1">Attachment URL <span className="text-slate-400 font-normal normal-case">(brochure / PDF)</span></label>
              <input value={form.attachmentUrl} onChange={(e) => set("attachmentUrl", e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" />
            </div>

            {/* Active toggle */}
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.isActive ? "bg-indigo-600" : "bg-slate-300"}`}
                onClick={() => set("isActive", !form.isActive)}>
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isActive ? "translate-x-5" : "translate-x-0"}`} />
              </div>
              <span className="text-sm font-medium text-slate-700">Active</span>
            </label>

            {/* Submit */}
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
              type="submit" disabled={saving}
              className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
              {saving ? (
                <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg> Saving...</>
              ) : (<>{isEdit ? <MdEdit /> : <MdAdd />} {isEdit ? "Update Event" : "Create Event"}</>)}
            </motion.button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Event Card ────────────────────────────────────────────────────────────────
function EventCard({ event, onEdit, onDelete }) {
  const isPast = new Date(event.eventDate) < new Date();
  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group">

      {/* Cover image or gradient banner */}
      <div className="relative h-36 overflow-hidden">
        {event.coverImageUrl ? (
          <img src={event.coverImageUrl} alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-400 flex items-center justify-center">
            <FaCalendarAlt className="text-white/30 text-6xl" />
          </div>
        )}
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <AudienceBadge audience={event.targetAudience} />
          {!event.isActive && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-800/70 text-white">Inactive</span>
          )}
          {isPast && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500/80 text-white">Past</span>
          )}
        </div>
        {/* Action buttons */}
        <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(event)}
            className="w-7 h-7 rounded-full bg-white/90 text-indigo-600 flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all shadow">
            <MdEdit className="text-sm" />
          </button>
          <button onClick={() => onDelete(event.id)}
            className="w-7 h-7 rounded-full bg-white/90 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow">
            <MdDelete className="text-sm" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-slate-800 line-clamp-1 mb-1">{event.title}</h3>
        <p className="text-xs text-slate-500 line-clamp-2 mb-3">{event.description}</p>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <MdCalendarToday className="text-indigo-500 flex-shrink-0" />
            <span>{fmtDateTime(event.eventDate)}</span>
            {event.eventEndDate && <span className="text-slate-400">→ {fmtDate(event.eventEndDate)}</span>}
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <MdLocationOn className="text-rose-500 flex-shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>

        {event.createdByUser && (
          <p className="text-xs text-slate-400 mt-2 truncate">
            by <span className="font-medium text-slate-500">{event.createdByUser.fullName}</span>
          </p>
        )}
      </div>
    </motion.div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function EventsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Filters & pagination
  const [audience, setAudience] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 12;
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchItems = async (pg = pageNo) => {
    setLoading(true);
    try {
      const params = {
        pageNo: pg, pageSize, activeOnly: false,
        ...(audience !== "all" && { audience }),
        ...(search && { searchParams: search }),
      };
      const res = await axios.get(`${server}/events`, {
        params, headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.count || 0);
    } catch {
      toast.error("Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchItems(pageNo); }, [token, audience, search, pageNo]); // eslint-disable-line
  useEffect(() => { setPageNo(1); }, [audience, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${server}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Event deleted.");
      fetchItems(pageNo);
    } catch { toast.error("Failed to delete."); }
  };

  const openCreate = () => { setSelectedEvent(null); setIsModalOpen(true); };
  const openEdit = (ev) => { setSelectedEvent(ev); setIsModalOpen(true); };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaCalendarAlt size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">School Events Calendar</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Schedule, publish, and manage school events, term holidays, and academic functions</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openCreate}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <MdAdd size={14} /> New Event
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Audience filter */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          <MdFilterList className="text-slate-400 ml-2" />
          {["all", ...AUDIENCES].map((a) => (
            <button key={a} onClick={() => setAudience(a)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${audience === a ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:bg-slate-100"}`}>
              {a}
            </button>
          ))}
        </div>

        {/* Search */}
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); }}
          className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative flex items-center">
            <MdSearch className="absolute left-3 text-slate-400 text-lg" />
            <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title, location..."
              className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm" />
            {searchInput && (
              <button type="button" onClick={() => { setSearchInput(""); setSearch(""); }}
                className="absolute right-2 text-slate-400 hover:text-slate-700">
                <MdClose />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-100 overflow-hidden animate-pulse">
              <div className="h-36 bg-slate-200" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-full" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400">
          <FaCalendarAlt className="text-6xl mb-4 text-slate-300" />
          <p className="text-lg font-semibold text-slate-500">No events found</p>
          <p className="text-sm mt-1">Create your first event to get started.</p>
          <button onClick={openCreate}
            className="mt-5 flex items-center gap-2 bg-indigo-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors">
            <MdAdd /> New Event
          </button>
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          <AnimatePresence>
            {items.map((ev) => (
              <EventCard key={ev.id} event={ev} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button onClick={() => setPageNo((p) => Math.max(1, p - 1))} disabled={pageNo === 1}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-indigo-50 hover:border-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
            <FaChevronLeft className="text-xs" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - pageNo) <= 2)
            .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
            .map((p, i) => p === "..." ? (
              <span key={i} className="text-slate-400 text-sm px-1">…</span>
            ) : (
              <button key={p} onClick={() => setPageNo(p)}
                className={`w-9 h-9 rounded-xl text-sm font-bold transition-all shadow-sm ${pageNo === p ? "bg-indigo-600 text-white shadow-indigo-200" : "bg-white border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:border-indigo-300"}`}>
                {p}
              </button>
            ))}
          <button onClick={() => setPageNo((p) => Math.min(totalPages, p + 1))} disabled={pageNo === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-indigo-50 hover:border-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      )}

      {/* Modal */}
      <EventModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
        onSuccess={() => fetchItems(1)} token={token} event={selectedEvent} />
    </div>
  );
}
